import { DatePicker, Form, message } from "antd";
import { useForm } from "antd/es/form/Form";
import Modal from "antd/es/modal/Modal";
import { useAuth } from "context/AuthContextProvider";
import { useLeavePreview } from "components/modals/LeaveConflictWarningModal";
import { useApplyLeaveRequest } from "hooks/mutations/staff";
import { useState } from "react";
import { translate } from "shared/languages";
import { ApplyLeaveForm } from "shared/types";
import { DEFAULT_DATE_FORMAT, now } from "shared/utils/dayjs";

interface NewLeaveModalProps {
  staffId: string;
  open: boolean;
  onCancel: () => void;
}

const NewLeaveModal = ({ staffId, open = false, onCancel }: NewLeaveModalProps) => {
  const [form] = useForm();
  const { state } = useAuth();
  const businessId = state.business?.id;
  const { mutate: applyLeave, mutateAsync: applyLeaveAsync } = useApplyLeaveRequest();
  const { checkAndConfirm, leavePreviewWarning } = useLeavePreview();
  // Guards the window between clicking Ajukan and the conflict-preview
  // check settling. Without it, a rapid double-click fires two concurrent
  // validateFields→checkAndConfirm chains, and when there's no conflict both
  // resolve straight to applyLeave, creating two duplicate leave records.
  // Only covers the CHECK itself (see useLeavePreview.checkAndConfirm):
  // once a real conflict opens the warning modal, that modal's own OK
  // button is the next actionable control, not this one.
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  const validateEndDate = (_: any, value: any) => {
    const startDate = form.getFieldValue("start_date");
    if (value && startDate && value.isBefore(startDate, "day")) {
      return Promise.reject(new Error(translate("leave.end_date_after_start")));
    }
    return Promise.resolve();
  };

  const onSubmit = () => {
    form
      .validateFields() // Validate form fields
      .then(async (values) => {
        const leaveForm: ApplyLeaveForm = {
          ...values,
          staff_id: staffId,
          business_id: businessId,
        };
        // Preview who this would affect first. Proceeds straight through
        // when nothing's affected, only gates behind the warning modal when
        // there's real impact to see (useLeavePreview, mirrors
        // useClashWarning's checkAndConfirm exactly).
        setIsCheckingConflicts(true);
        try {
          await checkAndConfirm(
            leaveForm,
            () => {
              applyLeave(leaveForm);
              onCancel();
            },
            // "Hapus Jadwal & Ajukan Cuti": awaited so the warning modal only
            // closes once the server has deleted the classes and saved the
            // leave (on failure it stays open with the error toast).
            async () => {
              await applyLeaveAsync({ ...leaveForm, remove_affected_schedules: true });
              onCancel();
            },
          );
        } finally {
          setIsCheckingConflicts(false);
        }
      })
      .catch(() => {
        message.error(translate("messages.error.invalid_form"));
      });
  };

  return (
    <>
      <Modal
        title={translate("leave.modal_title")}
        open={open}
        onCancel={onCancel}
        onOk={onSubmit}
        confirmLoading={isCheckingConflicts}
        afterClose={() => form.resetFields()}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="start_date"
            label={translate("leave.start_date")}
            rules={[{ required: true, message: translate("leave.start_date_required") }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder={translate("leave.start_date_placeholder")}
              format={DEFAULT_DATE_FORMAT}
              disabledDate={(current) => current && current < now().endOf("day")}
            />
          </Form.Item>
          <Form.Item
            name="end_date"
            label={translate("leave.end_date")}
            dependencies={["start_date"]}
            rules={[
              { required: true, message: translate("leave.end_date_required") },
              { validator: validateEndDate },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder={translate("leave.end_date_placeholder")}
              format={DEFAULT_DATE_FORMAT}
              disabledDate={(current) => current && current < now().endOf("day")}
            />
          </Form.Item>
        </Form>
      </Modal>
      {leavePreviewWarning}
    </>
  );
};
export default NewLeaveModal;
